import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import { Badge } from "../ui/badge";
import { useTRPC } from "@/lib/trpc";
import { useQuery, useMutation } from "@tanstack/react-query";
import { TurnstileWidget } from "../turnstile-widget";
import { toast } from "sonner";
import { useRouter } from "next/navigation";

interface PlanSelectionStepProps {
  onNext?: (planId: string) => void;
  onFreePlanComplete?: () => void;
}

export function PlanSelectionStep({ onNext, onFreePlanComplete }: PlanSelectionStepProps) {
  const [selectedPlan, setSelectedPlan] = useState("pro");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isCreatingFreeSubscription, setIsCreatingFreeSubscription] = useState(false);
  const trpc = useTRPC();
  const router = useRouter();

  const { data: plans, isLoading } = useQuery(trpc.subscriptions.getPlans.queryOptions());
  const { data: eligibility } = useQuery(
    trpc.subscriptions.checkFreePlanEligibility.queryOptions(undefined, {
      enabled: selectedPlan === "free",
    })
  );

  const createFreeSubscription = useMutation(trpc.subscriptions.createFreeSubscription.mutationOptions());

  const handleContinue = async () => {
    const plan = plans?.find(p => p.id === selectedPlan);
    if (!plan) return;

    // Handle free plan separately
    if (selectedPlan === "free") {
      if (!turnstileToken) {
        toast.error("Please complete the bot verification");
        return;
      }

      if (eligibility && !eligibility.eligible) {
        toast.error("You are not eligible for the free plan at this time");
        return;
      }

      setIsCreatingFreeSubscription(true);
      try {
        await createFreeSubscription.mutateAsync({
          turnstileToken,
        });
        toast.success("Free plan activated successfully!");
        if (onFreePlanComplete) {
          onFreePlanComplete();
        }
        router.replace("/dashboard");
      } catch (error: any) {
        const errorMessage = error.message || "Failed to create free subscription";
        if (errorMessage.includes("EMAIL_NOT_VERIFIED")) {
          toast.error("Please verify your email first");
        } else if (errorMessage.includes("TURNSTILE_FAILED")) {
          toast.error("Bot verification failed. Please try again");
          setTurnstileToken(null);
        } else if (errorMessage.includes("IP_RATE_LIMIT")) {
          toast.error("Too many signups from this location. Please try again later");
        } else if (errorMessage.includes("SUBSCRIPTION_ALREADY_EXISTS")) {
          toast.error("You already have a subscription");
        } else {
          toast.error(errorMessage);
        }
      } finally {
        setIsCreatingFreeSubscription(false);
      }
    } else {
      // Handle paid plans - continue to payment
      if (onNext) {
        onNext(plan.priceId);
      }
      router.push(`/onboarding/payment?planId=${encodeURIComponent(plan.priceId)}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <p className="text-muted-foreground">Failed to load subscription plans. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-3xl md:text-4xl font-mono">Welcome! Choose Your Plan</h1>
        <p className="text-muted-foreground">
          Get started with OCR processing that fits your needs
        </p>
        <p className="text-sm text-muted-foreground">
          You can always change your plan later from your dashboard
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {plans.map((plan) => (
          <Card
            key={plan.id}
            className={`relative p-6 cursor-pointer transition-all hover:shadow-lg ${
              selectedPlan === plan.id
                ? "ring-2 ring-primary shadow-lg"
                : "hover:border-muted-foreground/20"
            }`}
            onClick={() => setSelectedPlan(plan.id)}
          >
            {plan.id === 'free' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-green-600 text-white px-3 py-1 font-mono">
                  No Credit Card
                </Badge>
              </div>
            )}
            {plan.id === 'pro' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-foreground text-background px-3 py-1 font-mono">
                  Most Popular
                </Badge>
              </div>
            )}

            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className="text-xl font-mono">{plan.name}</h3>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
              </div>

              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-mono">${plan.price}</span>
                <span className="text-sm text-muted-foreground">/month</span>
              </div>

              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                  <span>{plan.tokens.toLocaleString()} pages {plan.id === 'free' ? 'total' : 'per month'}</span>
                </li>
                {plan.id === 'free' && (
                  <>
                    <li className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                      <span>Up to 5 tables</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                      <span>50 MiB storage</span>
                    </li>
                  </>
                )}
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                  <span>PDF document processing</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                  <span>Custom data extraction schemas</span>
                </li>
              </ul>

              <Button
                variant={selectedPlan === plan.id ? "default" : "outline"}
                className="w-full font-mono"
              >
                {selectedPlan === plan.id ? "Selected" : `Get Started with ${plan.name}`}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {selectedPlan === "free" && (
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="w-4 h-4" />
            <span>Please verify you're not a robot</span>
          </div>
          <TurnstileWidget
            onSuccess={(token) => setTurnstileToken(token)}
            onExpire={() => setTurnstileToken(null)}
            onError={() => {
              setTurnstileToken(null);
              toast.error("Verification failed. Please try again");
            }}
          />
          {eligibility && !eligibility.eligible && (
            <div className="text-center text-sm text-destructive">
              This location has reached the free plan signup limit. Please try again in {' '}
              {eligibility.nextEligibleAt
                ? new Date(eligibility.nextEligibleAt).toLocaleString()
                : '24 hours'}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-center pt-4">
        <Button
          onClick={handleContinue}
          size="lg"
          className="px-12 font-mono"
          disabled={
            (selectedPlan === "free" && !turnstileToken) ||
            (selectedPlan === "free" && eligibility && !eligibility.eligible) ||
            isCreatingFreeSubscription
          }
        >
          {isCreatingFreeSubscription ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating Free Account...
            </>
          ) : selectedPlan === "free" ? (
            "Activate Free Plan"
          ) : (
            "Continue to Payment"
          )}
        </Button>
      </div>
    </div>
  );
}
